import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Button, Icon } from '@factorialco/factorial-one-react'
import { Calendar, ExternalLink, People, Pin } from '@factorialco/factorial-one-react/icons/app'
import { OpenPositionFragment } from 'generated/resources/graphql'
import { kebabCase } from 'lodash'

import { useEffect } from 'modules/core/hooks/useEffect'
import { countryName } from 'modules/core/lib/countryList'
import { dateBackendDateTimeToRelativeTime } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { useCompanyPageSettings } from 'modules/company_page/hooks/useCompanyPageSettings'

import { InternalApplyModal } from './InternalApplyModal'
import * as styles from './OpenPositionsList.vanilla.css'
import { ReferCandidateModal } from './ReferCandidateModal'

interface Props {
  title: string
  openPositions: OpenPositionFragment[]
  companyPageReady: boolean
}

export const OpenPositionsList = ({ title, openPositions, companyPageReady }: Props) => {
  const { buildUrl } = useCompanyPageSettings()
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const sectionRef = React.useRef<HTMLDivElement>(null)
  const [openPositionReferral, setOpenPositionReferral] =
    React.useState<OpenPositionFragment | null>(null)

  const [openPositionToApply, setOpenPositionToApply] = React.useState<OpenPositionFragment | null>(
    null
  )

  const workplaceTypeLabelFor = (workplaceType: string | null | undefined) => {
    const workplaceTypes: Record<string, string> = {
      onsite: i18n.t('ats.workplace_types.onsite'),
      hybrid: i18n.t('ats.workplace_types.hybrid'),
      remote: i18n.t('ats.workplace_types.remote'),
    }

    return workplaceTypes[workplaceType ?? 'onsite']
  }

  const buildJobUrl = (positionId: number) => buildUrl(`job_posting/${positionId}`)

  const seeJobPosting = (openPosition: OpenPositionFragment) =>
    window.open(buildJobUrl(openPosition.id), '_blank')

  useEffect(() => {
    const id = search.replace('?', '')
    if (id === kebabCase(title) && sectionRef.current) {
      sectionRef.current.scrollIntoView()
      navigate(pathname, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Box ref={sectionRef} gap="s32">
      <Text.Normal color="grey1000" size="300" weight="semibold">
        {title}
      </Text.Normal>
      <Box component="ul" flexDirection="row" width="full" flexWrap="wrap" gap="s24">
        {openPositions.map((item) => {
          return (
            <Box key={item.id} component="li" minHeight="s200" className={styles.listItem}>
              <Box
                alignItems="flexStart"
                height="full"
                className={styles.listItem}
                borderRadius={{
                  all: 'abs016',
                }}
                border={{
                  all: {
                    color: 'grey400',
                    width: 's1',
                    style: 'solid',
                  },
                }}
                justifyContent="spaceBetween"
                flexGrow
              >
                <Box gap="s8" paddingBottom="s20" padding="s16" width="full">
                  <Box flexDirection="row" alignItems="flexStart" justifyContent="spaceBetween">
                    <Text.Normal color="grey1000" size="300" weight="semibold">
                      {item.title}
                    </Text.Normal>
                  </Box>
                  <Box gap="s6" flexDirection="row" alignItems="center" paddingTop="s2">
                    <Icon icon={People} />
                    <Text.Normal size="200">
                      {`${i18n.t('ats.open_positions.card.team_label')}: ${item.team?.name}`}{' '}
                    </Text.Normal>
                  </Box>
                  <Box gap="s6" flexDirection="row" alignItems="flexStart">
                    <Icon icon={Pin} />
                    <Text.Normal size="200">
                      {!item.location?.city
                        ? i18n.t('ats.open_positions.card.location_no_city_value', {
                            country: countryName(item.location?.country ?? undefined),
                            workPlaceType: workplaceTypeLabelFor(item.workplaceType),
                          })
                        : i18n.t('ats.open_positions.card.location_value', {
                            city: item.location?.city,
                            country: countryName(item.location?.country ?? undefined),
                            workPlaceType: workplaceTypeLabelFor(item.workplaceType),
                          })}
                    </Text.Normal>
                  </Box>
                  <Box gap="s6" flexDirection="row" alignItems="flexStart">
                    <Icon icon={Calendar} />
                    <Text.Normal size="200">
                      {i18n.t('ats.open_positions.actions.published_at', {
                        date: dateBackendDateTimeToRelativeTime(item.publishedAt),
                      })}
                    </Text.Normal>
                  </Box>
                </Box>

                <Box
                  gap="s8"
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="spaceBetween"
                  width="full"
                  paddingX="s16"
                  paddingY="s12"
                  border={{
                    top: { color: 'grey400', width: 's1', style: 'solid' },
                  }}
                >
                  {item.allowInternalApply && (
                    <Button
                      variant="outline"
                      label={i18n.t('ats.open_positions.actions.apply')}
                      onClick={() => setOpenPositionToApply(item)}
                    />
                  )}

                  <Box flexDirection="row" justifyContent="flexEnd" gap="s8" flexGrow>
                    {companyPageReady && (
                      <Button
                        hideLabel
                        icon={ExternalLink}
                        label="See job posting"
                        onClick={() => void seeJobPosting(item)}
                        variant="outline"
                        round
                      />
                    )}
                    {item.allowReferral && (
                      <Button
                        onClick={() => setOpenPositionReferral(item)}
                        label={i18n.t('ats.open_positions.actions.refer')}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          )
        })}
        {openPositionReferral && (
          <ReferCandidateModal
            openPosition={openPositionReferral}
            onClose={() => setOpenPositionReferral(null)}
          />
        )}
        {openPositionToApply && (
          <InternalApplyModal
            openPosition={openPositionToApply}
            onClose={() => setOpenPositionToApply(null)}
          />
        )}
      </Box>
    </Box>
  )
}
